'use client';
import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useWebSocket } from '@/hooks/useWebSocket';
import { X, Minus, Send, Paperclip, Smile, Download, Loader2 } from 'lucide-react';

interface Contact {
  _id: string;
  firstName: string;
  lastName: string;
  name: string;
  email: string;
  profilePic: string;
  department: string;
  designation: string;
  status: string;
  isOnline: boolean;
  allowChat: boolean;
  lastSeen?: string | null;
}

interface FileData {
  fileName: string;
  fileUrl: string;
  fileSize: number;
  mimeType: string;
}

interface Message {
  id: string;
  chatId: string;
  sender: {
    id: string;
    firstName: string;
    lastName: string;
    profilePic: string;
  };
  content: string;
  messageType: 'text' | 'file' | 'image' | 'system';
  fileData?: FileData;
  isEdited?: boolean;
  isDeleted?: boolean;
  readBy?: Array<{ employee: string; readAt: string }>;
  createdAt: string;
}

interface FloatingChatWindowProps {
  contact: Contact;
  chatId: string;
  position: number;
  onClose: () => void;
  onMinimize: () => void;
  isMinimized: boolean;
}

// Avatar component (inline)
const Avatar = ({ src, name, size = 'sm', showOnline = false, isOnline = false }: {
  src?: string;
  name: string;
  size?: 'xs' | 'sm' | 'md';
  showOnline?: boolean;
  isOnline?: boolean;
}) => {
  const [imageError, setImageError] = useState(false);

  const sizeClasses = {
    xs: 'w-6 h-6 text-xs',
    sm: 'w-8 h-8 text-xs',
    md: 'w-10 h-10 text-sm'
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.length >= 2
      ? `${parts[0].charAt(0)}${parts[parts.length - 1].charAt(0)}`
      : name.charAt(0);
  };

  const getBgColor = (name: string) => {
    const colors = [
      'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-yellow-500',
      'bg-purple-500', 'bg-pink-500', 'bg-indigo-500', 'bg-teal-500'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return colors[hash % colors.length];
  };

  return (
    <div className="relative">
      {src && !imageError ? (
        <img
          src={src}
          alt={name}
          className={`${sizeClasses[size]} rounded-full object-cover`}
          onError={() => setImageError(true)}
        />
      ) : (
        <div className={`${sizeClasses[size]} rounded-full ${getBgColor(name)} flex items-center justify-center text-white font-semibold`}>
          {getInitials(name).toUpperCase()}
        </div>
      )}
      {showOnline && (
        <div className={`absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 ${isOnline ? 'bg-green-500' : 'bg-gray-400'} rounded-full border-2 border-white`} />
      )}
    </div>
  );
};

// Helper to format file sizes in human-readable form
function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function FloatingChatWindow({
  contact,
  chatId,
  position,
  onClose,
  onMinimize,
  isMinimized
}: FloatingChatWindowProps) {
  const { user } = useAuth();
  const { subscribe, sendMessage, sendTyping, sendReadReceipt, onlineUsers } = useWebSocket();

  const [messages, setMessages] = useState<Message[]>([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [typingUser, setTypingUser] = useState<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<NodeJS.Timeout | null>(null);
  const isFocusedRef = useRef(!isMinimized);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const rightPosition = 80 + (position * 340);
  const isContactOnline = contact._id !== user?._id && onlineUsers.some(id => id === contact._id);

  // Load initial messages on mount
  useEffect(() => {
    if (chatId && !isMinimized) {
      loadMessages();
    }
  }, [chatId, isMinimized]);

  // Subscribe to WebSocket events
  useEffect(() => {
    if (!chatId) return;

    const unsubNewMessage = subscribe('new_message', (data: any) => {
      const incomingChatId = (data.chatId || data.message?.chatId || '').toString();
      if (incomingChatId !== chatId.toString()) return;
      const msg = data.message;
      const msgId = msg?.id || msg?._id;
      if (!msgId) return;
      setMessages(prev => {
        if (prev.some(m => m.id === msgId.toString())) return prev;
        // Normalize the message shape — sender might be just an ObjectId or a populated object
        const senderObj = typeof msg.sender === 'object' && msg.sender !== null
          ? { id: msg.sender.id || msg.sender._id, firstName: msg.sender.firstName || '', lastName: msg.sender.lastName || '', profilePic: msg.sender.profilePic || '' }
          : { id: msg.sender, firstName: '', lastName: '', profilePic: '' };
        return [...prev, { ...msg, id: msgId.toString(), sender: senderObj, createdAt: msg.createdAt || new Date().toISOString() }];
      });
      setTimeout(scrollToBottom, 100);

      // Auto-send read receipt if window is focused
      if (isFocusedRef.current && (typeof msg.sender === 'string' ? msg.sender : msg.sender?.id || msg.sender?._id) !== user?._id) {
        sendReadReceipt(chatId, [msgId.toString()]);
      }
    });

    // Handle sender's own message acknowledgment
    const unsubMessageSent = subscribe('message_sent', (data: any) => {
      if (!data.message) return;
      const msg = data.message;
      const msgChatId = (msg.chatId || '').toString();
      if (msgChatId !== chatId.toString()) return;
      setMessages(prev => {
        const msgId = (msg.id || msg._id || '').toString();
        if (!msgId || prev.some(m => m.id === msgId)) return prev;
        const userName = user?.name || '';
        const nameParts = userName.split(' ');
        return [...prev, {
          ...msg,
          id: msgId,
          sender: { id: user?._id, firstName: nameParts[0] || '', lastName: nameParts.slice(1).join(' ') || '', profilePic: '' },
          createdAt: msg.createdAt || new Date().toISOString()
        }];
      });
      setTimeout(scrollToBottom, 100);
    });

    const unsubEdited = subscribe('message_edited', (data: any) => {
      if ((data.chatId || '').toString() !== chatId.toString()) return;
      const editedId = (data.messageId || '').toString();
      setMessages(prev => prev.map(m =>
        m.id === editedId
          ? { ...m, content: data.content, isEdited: true }
          : m
      ));
    });

    const unsubDeleted = subscribe('message_deleted', (data: any) => {
      if ((data.chatId || '').toString() !== chatId.toString()) return;
      const deletedId = (data.messageId || '').toString();
      setMessages(prev => prev.map(m =>
        m.id === deletedId
          ? { ...m, isDeleted: true, content: 'This message was deleted' }
          : m
      ));
    });

    const unsubTyping = subscribe('typing', (data: any) => {
      if ((data.chatId || '').toString() !== chatId.toString()) return;
      if (data.employeeId === user?._id) return;
      setTypingUser(data.employeeName || contact.name);

      // Clear after 3 seconds of no typing events
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      typingTimerRef.current = setTimeout(() => setTypingUser(null), 3000);
    });

    const unsubReadReceipt = subscribe('read_receipt', (data: any) => {
      if ((data.chatId || '').toString() !== chatId.toString()) return;
      const receiptMsgId = (data.messageId || '').toString();
      setMessages(prev => prev.map(m => {
        if (m.id === receiptMsgId) {
          const existing = m.readBy || [];
          if (existing.some((r: any) => r.employee === data.readBy?.employee)) return m;
          return { ...m, readBy: [...existing, data.readBy] };
        }
        return m;
      }));
    });

    return () => {
      unsubNewMessage();
      unsubMessageSent();
      unsubEdited();
      unsubDeleted();
      unsubTyping();
      unsubReadReceipt();
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
    };
  }, [chatId, user?._id, subscribe, sendReadReceipt, contact.name]);

  // Track focus state
  useEffect(() => {
    isFocusedRef.current = !isMinimized;
    // Send read receipts for unread messages when becoming focused
    if (!isMinimized && messages.length > 0 && user) {
      const unreadIds = messages
        .filter(m => m.sender?.id !== user._id && !(m.readBy || []).some(r => r.employee === user._id))
        .map(m => m.id);
      if (unreadIds.length > 0) {
        sendReadReceipt(chatId, unreadIds);
      }
    }
  }, [isMinimized]);

  const loadMessages = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/chats/${chatId}/messages`);
      if (response.ok) {
        const data = await response.json();
        // Normalize message shape: ensure `id` and `sender.id` fields exist
        const normalized = (data.messages || []).map((m: any) => ({
          ...m,
          id: m.id || m._id,
          sender: m.sender ? { ...m.sender, id: m.sender.id || m.sender._id } : m.sender,
        }));
        setMessages(normalized);
        setTimeout(scrollToBottom, 100);
      }
    } catch (error) {
      console.error('Failed to load messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = useCallback(() => {
    if (!newMessage.trim() || !chatId) return;
    sendMessage(chatId, newMessage.trim(), 'text');
    setNewMessage('');
  }, [newMessage, chatId, sendMessage]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setNewMessage(e.target.value);
    sendTyping(chatId);
  };

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !chatId) return;

    // Reset file input so same file can be selected again
    e.target.value = '';

    setIsUploading(true);
    setUploadError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch('/api/chat/upload', {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Upload failed');
      }

      const data = await response.json();
      if (data.success && data.file) {
        const { fileName, fileUrl, fileSize, mimeType } = data.file;
        const messageType = mimeType.startsWith('image/') ? 'image' : 'file';
        sendMessage(chatId, fileName, messageType, { fileName, fileUrl, fileSize, mimeType });
      }
    } catch (error: any) {
      setUploadError(error.message || 'Failed to upload file');
      // Auto-clear error after 5 seconds
      setTimeout(() => setUploadError(null), 5000);
    } finally {
      setIsUploading(false);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const formatTime = (date: string) => {
    return new Date(date).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    });
  };

  const getReadStatus = (message: Message): 'sent' | 'read' => {
    if (message.sender?.id !== user?._id) return 'sent';
    const readBy = message.readBy || [];
    const othersRead = readBy.some(r => r.employee !== user?._id);
    return othersRead ? 'read' : 'sent';
  };

  return (
    <div
      className="fixed bottom-0 bg-white shadow-2xl border border-gray-200 rounded-t-lg transition-all duration-200 z-40"
      style={{
        right: `${rightPosition}px`,
        width: '320px',
        height: isMinimized ? '40px' : '400px'
      }}
    >
      {/* Header */}
      <div
        className="flex items-center justify-between p-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg cursor-pointer"
        onClick={onMinimize}
      >
        <div className="flex items-center flex-1 min-w-0">
          <Avatar
            src={contact.profilePic}
            name={contact.name}
            size="sm"
            showOnline
            isOnline={isContactOnline}
          />
          <div className="ml-2 flex-1 min-w-0">
            <h4 className="font-medium text-sm truncate">{contact.name}</h4>
            <p className="text-xs text-blue-200">
              {isContactOnline ? 'Active now' : (contact.lastSeen ? `Last seen ${new Date(contact.lastSeen).toLocaleString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true })}` : 'Offline')}
            </p>
          </div>
        </div>

        <div className="flex items-center space-x-1 ml-2">
          <button
            onClick={(e) => { e.stopPropagation(); onMinimize(); }}
            className="p-1 hover:bg-blue-800 rounded transition-colors"
            title={isMinimized ? 'Maximize' : 'Minimize'}
          >
            <Minus size={14} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onClose(); }}
            className="p-1 hover:bg-blue-800 rounded transition-colors"
            title="Close"
          >
            <X size={14} />
          </button>
        </div>
      </div>

      {/* Chat body */}
      {!isMinimized && (
        <div className="h-80 flex flex-col">
          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50 scrollbar-thin scrollbar-thumb-gray-300">
            {loading ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto mb-2" />
                  <p className="text-xs text-gray-500">Loading messages...</p>
                </div>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="w-12 h-12 mx-auto mb-3 bg-blue-100 rounded-full flex items-center justify-center">
                    <Send className="w-6 h-6 text-blue-500" />
                  </div>
                  <p className="text-sm font-medium text-gray-600">Start the conversation</p>
                  <p className="text-xs text-gray-500">Send a message to get started</p>
                </div>
              </div>
            ) : (
              <>
                {messages.map((message, index) => {
                  const isOwn = message.sender?.id === user?._id;
                  const senderName = `${message.sender?.firstName || ''} ${message.sender?.lastName || ''}`.trim();
                  const showAvatar = !isOwn && (index === 0 || messages[index - 1].sender?.id !== message.sender?.id);

                  if (message.isDeleted) {
                    return (
                      <div key={message.id} className={`flex ${isOwn ? 'justify-end' : 'justify-start'}`}>
                        <div className="px-3 py-2 rounded-lg text-sm italic text-gray-400 bg-gray-100 border border-gray-200">
                          This message was deleted
                        </div>
                      </div>
                    );
                  }

                  return (
                    <div
                      key={message.id}
                      className={`flex items-end space-x-2 ${isOwn ? 'justify-end' : 'justify-start'}`}
                    >
                      {!isOwn && (
                        <div className="flex-shrink-0 w-6">
                          {showAvatar ? (
                            <Avatar src={message.sender?.profilePic} name={senderName || 'Unknown'} size="xs" />
                          ) : <div className="w-6" />}
                        </div>
                      )}

                      <div className={`max-w-[200px] ${isOwn ? 'order-1' : ''}`}>
                        {!isOwn && showAvatar && (
                          <p className="text-xs text-gray-500 mb-1 px-1">{senderName}</p>
                        )}
                        <div
                          className={`px-3 py-2 rounded-lg text-sm shadow-sm ${
                            isOwn
                              ? 'bg-blue-600 text-white rounded-br-md'
                              : 'bg-white text-gray-900 rounded-bl-md border border-gray-200'
                          }`}
                        >
                          {/* Image message */}
                          {message.messageType === 'image' && message.fileData?.fileUrl ? (
                            <div>
                              <img
                                src={message.fileData.fileUrl}
                                alt={message.fileData.fileName || 'Image'}
                                className="max-w-full rounded cursor-pointer"
                                style={{ maxHeight: '200px' }}
                              />
                              <p className="text-xs mt-1 opacity-75">{message.fileData.fileName}</p>
                            </div>
                          ) : message.messageType === 'file' && message.fileData ? (
                            /* File message */
                            <a
                              href={message.fileData.fileUrl}
                              download={message.fileData.fileName}
                              className={`flex items-center gap-2 ${isOwn ? 'text-blue-100 hover:text-white' : 'text-blue-500 hover:text-blue-700'} underline`}
                            >
                              <Download size={14} />
                              <span className="truncate">{message.fileData.fileName}</span>
                              <span className="text-xs whitespace-nowrap">({formatFileSize(message.fileData.fileSize)})</span>
                            </a>
                          ) : (
                            /* Text message */
                            <p className="leading-relaxed break-words">{message.content}</p>
                          )}
                          <div className={`flex items-center justify-end gap-1 mt-1 ${isOwn ? 'text-blue-200' : 'text-gray-500'}`}>
                            {message.isEdited && (
                              <span className="text-[10px] italic">edited</span>
                            )}
                            <span className="text-[10px]">{formatTime(message.createdAt)}</span>
                            {isOwn && (
                              <span className="text-[10px]">
                                {getReadStatus(message) === 'read' ? '✓✓' : '✓'}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}

                {/* Typing Indicator */}
                {typingUser && (
                  <div className="px-3 py-2">
                    <div className="flex items-center space-x-2">
                      <div className="flex space-x-1">
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }} />
                        <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                      </div>
                      <p className="text-xs text-gray-500">{typingUser} is typing...</p>
                    </div>
                  </div>
                )}

                <div ref={messagesEndRef} />
              </>
            )}
          </div>

          {/* Input */}
          <div className="border-t border-gray-200 p-3 bg-white">
            {/* Upload error toast */}
            {uploadError && (
              <div className="mb-2 px-3 py-1.5 bg-red-50 border border-red-200 text-red-600 text-xs rounded-lg flex items-center justify-between">
                <span>{uploadError}</span>
                <button onClick={() => setUploadError(null)} className="ml-2 text-red-400 hover:text-red-600">
                  <X size={12} />
                </button>
              </div>
            )}
            <div className="flex items-end space-x-2">
              {/* Hidden file input */}
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileUpload}
                className="hidden"
                aria-label="Upload file"
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={isUploading}
                className="text-gray-500 hover:text-gray-700 transition-colors p-1 rounded disabled:opacity-50"
                title="Attach file"
              >
                {isUploading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Paperclip size={16} />
                )}
              </button>

              <div className="flex-1 relative">
                <input
                  type="text"
                  value={newMessage}
                  onChange={handleInputChange}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSend(); } }}
                  placeholder="Type a message..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent pr-8"
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors"
                  title="Add emoji"
                >
                  <Smile size={16} />
                </button>
              </div>

              <button
                onClick={handleSend}
                disabled={!newMessage.trim()}
                className="bg-blue-600 text-white rounded-full p-2 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                title="Send message"
              >
                <Send size={14} />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
